import { format, getYear, startOfQuarter, endOfQuarter, max, subYears, startOfDay, addDays } from 'date-fns';
import { relationshipModel, timeModel } from "../../models";
import { RecurrencePeriodTypes } from "../../models/service/model";
import compact from 'lodash/compact';
import { sendErrReport } from "../../infra/utils";
import { getMaxFutureInvoiceDate } from "../../business-logic/billingLogic";
export const selectorWidth = '320px';
export let BillMode;

(function (BillMode) {
  BillMode["Now"] = "now";
  BillMode["Upcoming"] = "upcoming";
  BillMode["FutureDate"] = "futureDate";
})(BillMode || (BillMode = {}));

export let FormKey; // Must extend { note: string; issueDate: Date }.
// I can't find a way to type it here.

(function (FormKey) {
  FormKey["IssueDate"] = "issueDate";
  FormKey["Note"] = "note";
})(FormKey || (FormKey = {}));

export const ctaTextMap = {
  now: 'Bill now',
  upcoming: 'Add to bill',
  futureDate: 'Bill'
};
export const getFormattedPreviousPeriodValue = (recurrencePeriod, startDate, endDate) => {
  return recurrencePeriod === RecurrencePeriodTypes.Yearly ? getYear(startDate).toString() : recurrencePeriod === RecurrencePeriodTypes.Quarterly ? "".concat(format(startOfQuarter(endDate), 'MMMM'), " - ").concat(format(endOfQuarter(endDate), 'MMMM')) : "".concat(format(startDate, 'LLL do, Y'), " - ").concat(format(endDate, 'LLL do, Y'));
};
export function getDateRange(mode, manualServicesBilling, now, effectiveDate) {
  switch (mode) {
    case BillMode.Upcoming:
      return {};

    case BillMode.Now:
      {
        const today = startOfDay(now);
        return {
          maxDate: today,
          minDate: manualServicesBilling ? manualServicesBilling.minDate : max(compact([subYears(today, 1), effectiveDate]))
        };
      }

    case BillMode.FutureDate:
      {
        return {
          minDate: max(compact([effectiveDate, addDays(startOfDay(now), 1)])),
          maxDate: getMaxFutureInvoiceDate(now)
        };
      }
  }
}
export function getMinDateMessage(minDate) {
  return minDate ? "Date should not be before ".concat(format(minDate, 'LLL do, Y')) : undefined;
}
export function getMaxDateMessage(maxDate) {
  return maxDate ? "Date should not be after ".concat(format(maxDate, 'LLL do, Y')) : undefined;
}
export function getDefaultIssueDate(mode, invoiceDateInfo, now, effectiveDate) {
  switch (mode) {
    case BillMode.Now:
      return startOfDay(now);

    case BillMode.Upcoming:
      return invoiceDateInfo ? invoiceDateInfo.futureInvoicesDates[0] : startOfDay(now);

    case BillMode.FutureDate:
      {
        if (invoiceDateInfo && !invoiceDateInfo.isUpcomingSelected) {
          return invoiceDateInfo.selectedDate;
        } else {
          const {
            minDate
          } = getDateRange(mode, undefined, now, effectiveDate);

          if (!minDate) {
            const error = new Error('no default issueDate');
            sendErrReport(error);
            throw error;
          }

          return minDate;
        }
      }
  }
}
export function normalizeEffectiveDate(effectiveDate) {
  return !effectiveDate ? null : effectiveDate instanceof Date ? effectiveDate : timeModel.fromDatestampObject(effectiveDate);
}
export function getBillModeOptions(now, effectiveDate, invoiceDateInfo) {
  return compact([{
    value: BillMode.Upcoming,
    text: 'Add to upcoming invoice',
    disabled: !invoiceDateInfo,
    tooltip: !invoiceDateInfo ? 'There is no upcoming invoice' : undefined
  }, !relationshipModel.isFutureRelationship({
    effectiveDate
  }, now) && {
    value: BillMode.Now,
    text: 'Bill now'
  }, {
    value: BillMode.FutureDate,
    text: 'Bill on date'
  }]);
}
export function getDefaultBillMode(billModeOptions, invoiceDateInfo) {
  if (invoiceDateInfo !== null && invoiceDateInfo !== void 0 && invoiceDateInfo.isUpcomingSelected && hasEnabledBillModeOption(billModeOptions, BillMode.Upcoming)) {
    return BillMode.Upcoming;
  } else if (invoiceDateInfo && !invoiceDateInfo.isUpcomingSelected && hasEnabledBillModeOption(billModeOptions, BillMode.FutureDate)) {
    return BillMode.FutureDate;
  } else if (hasEnabledBillModeOption(billModeOptions, BillMode.Now)) {
    return BillMode.Now;
  } else {
    const firstEnabledOption = billModeOptions.find(opt => !opt.disabled);

    if (!firstEnabledOption) {
      const error = new Error('no enabled billMode options');
      sendErrReport(error);
      throw error;
    }

    return firstEnabledOption.value;
  }
}

function hasEnabledBillModeOption(billModeOptions, billMode) {
  return billModeOptions.some(opt => opt.value === billMode && !opt.disabled);
}